// src/constants/farmConstants.js
// Shared configuration for the Farm marketplace page.

/** Product statuses that should be excluded from the public marketplace feed */
export const EXCLUDED_PRODUCT_STATUSES = ['sold', 'deleted', 'draft'];

/**
 * Canonical sold state for a product.
 *
 * Every finalization path (deal completion in Cloud Functions, mutual offer
 * ratings in Cloud Functions, and the client-side mutual-rating write) writes
 * BOTH the `sold` boolean and the `status` string. Readers therefore never have
 * to guess which representation a finalized product carries.
 */
export const PRODUCT_SOLD_STATUS = 'sold';
export const PRODUCT_ACTIVE_STATUS = 'active';

/**
 * True when a product is finalized as sold, under either representation.
 * @param {object} product product document.
 * @return {boolean}
 */
export function isProductSold(product) {
  if (!product) return false;
  return product.sold === true || product.status === PRODUCT_SOLD_STATUS;
}

/** Maximum products fetched in a single load batch */
export const FARM_FETCH_LIMIT = 500;

/** Number of products rendered per infinite-scroll page */
export const FARM_PAGE_SIZE = 20;

/** Sort options available on the Farm page */
export const FARM_SORT_OPTIONS = [
  { value: 'newest', labelKey: 'filters.newest' },
  { value: 'priceLow', labelKey: 'filters.priceLow' },
  { value: 'priceHigh', labelKey: 'filters.priceHigh' },
  { value: 'closest', labelKey: 'filters.closest' },
];

/** Product visibility check – returns true when the product should appear in the public feed */
export function isProductVisible(product) {
  if (!product) return false;
  // Must not be hidden/unlisted
  if (product.hidden === true || product.isHidden === true) return false;
  // Must not be private
  if (product.isPrivate === true) return false;
  // Must not be sold
  if (isProductSold(product)) return false;
  // Must not be deleted
  if (product.deleted === true || product.status === 'deleted') return false;
  // Must not be draft
  if (product.isDraft === true || product.status === 'draft') return false;
  // Exclude explicitly inactive
  if (product.status === 'inactive') return false;
  return true;
}
